<template>
  <v-row justify="space-between">
    <v-col cols="12">
      <div class="text-right mb-2">
        <v-btn href="#" @click="exportCsv('issuedBookingList')" small>
          <v-icon small round>mdi-file-download</v-icon>
          export
        </v-btn>
      </div>
      <v-data-table
          ref="issuedBookingList"
          :loading="listing.loading"
          :headers="listingHeaders"
          :items="listing.bookings"
          :options.sync="listing.pagination"
          :footer-props="{
            'items-per-page-options': listing.rowsPerPageItems,
            'items-per-page-text': 'Lignes par page',
          }"
          item-key="bookingId"
          no-data-text="Aucun dossier"
          class="elevation-1 tbl"
          @click:row="onBookingRowClick"
      >
        <template #item.siteName="{ item }">
            <span class="caption grey--text text--darken-1">{{ item.bookingId }}</span>
            <site-label :sitename="item.siteName" />
        </template>

        <template #item.bookingDate="{ value }">
          <p>
            {{
              _parseAndFormatDate(
                  value,
                  datePatternConfig.serverLocalDateTime,
                  "dd/MM/yy"
              )
            }}
          </p>
          <p class="caption grey--text text--darken-1">
            {{
              _parseAndFormatDate(
                  value,
                  datePatternConfig.serverLocalDateTime,
                  datePatternConfig.fr.time
              )
            }}
          </p>
        </template>

        <template #item.startDate="{ item }">
          <p>
            {{
              _parseAndFormatDate(
                  item.startDate,
                  datePatternConfig.serverLocalDateTime,
                  "dd/MM/yy"
              )
            }}
          </p>
          <p class="caption grey--text text--darken-1">
            {{
              _parseAndFormatDate(
                  item.startDate,
                  datePatternConfig.serverLocalDateTime,
                  datePatternConfig.fr.time
              )
            }} • {{ item.duration }}n
          </p>
        </template>

        <template #item.customer="{ item }">
            <p v-if="item.customer && item.customer !== ''">
              {{ item.customer }}
            </p>
            <span
                class="caption grey--text text--darken-1"
                v-for="(guest, i) in item.guests"
                :key="i + '-guest'"
            >{{
                $tc(guest.ageClass + "-count-small", guest.size, {
                  size: guest.size,
                })
              }}
            </span>
            <span v-if="item.guestsName" class="caption grey--text">- {{ item.guestsName }}</span>
        </template>

        <template #item.hotelWithDesti="{ value }">
          <p class="small-text" v-for="hotel in value" :key="`hotel-${hotel[0]}`" >
            <span class="grey--text text--darken-1">{{hotel[0]}} - </span>{{hotel[1]}}
          </p>
        </template>

        <template #item.airlineCodes="{ value }">
          <span class="grey--text text--darken-1">{{value.length > 0 ? value.join(" ") : '-' }}</span>
        </template>

        <template #item.transferName="{ item }">
            <v-tooltip top v-if="item.transferName">
              <template #activator="{ on }" >
                <v-icon v-on="on" :size="16">mdi-bus-side</v-icon>
              </template>
              <span>{{ item.transferName }}</span>
            </v-tooltip>
            <v-tooltip top v-if="item.carrentName">
              <template #activator="{ on }">
                <v-icon v-on="on" :size="15">mdi-car-side</v-icon>
              </template>
              <span>{{ item.carrentName }}</span>
            </v-tooltip>
            <span class="grey--text text--darken-1" v-if="!item.transferName && !item.carrentName">-</span>
        </template>

        <template #item.supplierAccountingNames="{ item }">
          <p v-for="(name, index) in getIssuesSupplierAccountingNames(item)"
             :key="`${item.bookingId}-supplier-accounting-name-${index}`"
             class="suppliers-names"
          >
            {{name}}
          </p>
        </template>

        <template #item.ca="{ item }">
            <v-tooltip top v-if="item.withEasyBooking">
              <template #activator="{ on }">
                <v-icon small color="orange lighten-1" v-on="on">mdi-numeric-1-circle-outline</v-icon>
              </template>
              Paiement à 1€
            </v-tooltip>
            <v-tooltip top v-if="item.isPledg">
              <template #activator="{ on }">
                <v-icon v-on="on" small color="indigo darken-1">mdi-credit-card-multiple-outline</v-icon>
              </template>
              Paiement Pledg
            </v-tooltip>
            <v-tooltip top v-if="item.creationCtx == 'payment'">
              <template #activator="{ on }">
                <v-icon small color="blue" v-on="on">mdi-cart-arrow-right</v-icon>
              </template>
              Créé dans le tunnel de paiement
            </v-tooltip>
            <v-tooltip top v-if="item.creationCtx == 'cart'">
              <template #activator="{ on }">
                <v-icon small color="blue" v-on="on">mdi-cart</v-icon>
              </template>
              Créé dans le panier
            </v-tooltip>
            <v-tooltip top v-if="item.paymentFailed">
              <template #activator="{ on }">
                <v-icon small color="red" v-on="on"
                >mdi-credit-card-off
                </v-icon
                >
              </template>
              Dernier paiement en échec
            </v-tooltip>
            {{ item.ca | currency("EUR") }}
            <div class="body-2 grey--text">
              {{ item.purchase | currency("EUR") }}
              <span class="caption ml-1 grey--text text--lighten-1">
                {{ item.markup | percent }}
              </span>
            </div>
        </template>

        <template #item.bookingStatus="{ item }">
          <availability v-model="item.bookingStatus" kind="BookingStatus" />
          <availability
              v-model="item.supplierStatus"
              kind="SupplierStatus"
              v-if="item.supplierStatus"
          />
          <v-tooltip top v-for="(tag, index) in item.tags" :key="index">
            <template #activator="{ on }">
              <v-chip v-on="on" small label class="pink white--text mx-1">
                <v-icon left dark class="mr-0 pr-1">mdi-tag</v-icon>
                {{ tag }}
              </v-chip>
            </template>
            {{ tag }}
          </v-tooltip>
        </template>

        <template #item.locked="{ item }">
          <v-icon small v-if="item.locked">mdi-lock</v-icon>
          <template v-if="item.optionDate">
            <v-tooltip top>
              <template #activator="{ on }">
                <v-icon v-on="on" small class="mr-1">mdi-clock-outline</v-icon>
              </template>
              option jusqu'au {{ item.optionDate }}
            </v-tooltip>
          </template>
        </template>

        <template #item.prestationIssues="{ item }">
          <div class="d-flex flex-row gap1 justify-center align-center">
            <v-tooltip left v-for="chip in getIssueStatusChips(item.prestationIssues)" :key="chip.status">
              <template #activator="{ on }">
                <v-chip v-on="on" @click.stop class="font-weight-bold issue-chip" block x-small :color="chip.color" text-color="white">
                  {{ chip.count }}
                </v-chip>
              </template>
              <div class="py-1 pr-1 issue-tooltip relative">
                <div v-for="(issue, index) in chip.issues" :key="`${index}-prestation-issue-${issue.id}`" class="mb-3">
                  <v-divider v-if="index !== 0" :color="'#BDBDBD'" class="mb-3" />
                  <v-chip class="font-weight-bold issue-label-chip" small
                      :color="chip.color" text-color="white">
                    <span class="issue-label-text">{{ issue.label }}</span>
                    <span class="caption-xs ml-1 issue-label-num">
                      <span class="tiny-text">#</span>{{ issue.id }}</span>
                  </v-chip>

                  <div v-if="issue.comments && issue.comments.length" class="mt-2">
                    <div v-for="(comment, index) in issue.comments" :key="issue.id + '-comment-' + index">
                      <div v-if="index !== 0" class="px-2 mb-1">
                        <v-divider  :color="'#A0A0A0'"  />
                      </div>
                      <div class="issue-comment-row mt-1">
                        <div class="issue-comment-text caption pl-2">
                          {{ comment.comment }}
                        </div>

                        <div class="issue-comment-meta">
                          <p class="caption font-weight-bold text-right grey--text text--lighten-3">
                            {{ comment.username }}
                          </p>
                          <p class="caption-xs text-right grey--text text--lighten-2">
                            <span>{{ _convertDay(comment.createdAt) }}</span>
                            <span class="caption-xs"> à </span>
                            <span class="">{{ _convertHour(comment.createdAt) }}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="caption grey--text text--lighten-1 mt-1">
                    Aucun commentaire
                  </div>
                </div>
              </div>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
import { post } from "@/utils/api";
import SiteLabel from "@/components/commons/SiteLabel.vue";
import Availability from "@/components/commons/Availability.vue";
import ExportMixin from "@/components/mixins/ExportMixin";

export default {
  name: "BookingPrestaIssueListTable",
  components: {Availability, SiteLabel},
  mixins: [ExportMixin],
  emits: ['loading'],
  mounted() {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('blur', this.onWindowBlur)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('blur', this.onWindowBlur)
  },
  data() {
    return {
      isCtrlOrMetaPressed: false,
      listing: {
        loading: false,
        rowsPerPageItems: [{text: "Tous", value: -1}, 50, 200],
        pagination: {
          options: {
            page: 1,
            itemsPerPage: 200,
            sortBy: [],
            sortDesc: [false],
          },
        },
        issueStatusChips: [
          {
            status: 'in_progress',
            color: 'red',
          },
          {
            status: 'closed',
            color: 'blue',
          },
          {
            status: 'give_up',
            color: 'grey',
          },
        ],
        bookings: [],
      },
    }
  },
  watch: {},
  computed: {
    listingHeaders() {
      const filterHeaders = (allowedRoles) =>
          this.userRolesIds.some((r) => allowedRoles.includes(r));

      const defaultRolesAccess = [
        this.roles.admin,
        this.roles.onplaceagent,
        this.roles.hlItaly,
      ];
      const headers = [
        {
          text: "SITE",
          value: "siteName",
          align: "left",
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "RÉSA",
          value: "bookingDate",
          align: "left",
          allowedRoles: defaultRolesAccess,
          sort: this.sortByDateTime,
        },
        {
          text: "SÉJOUR",
          value: "startDate",
          align: "left",
          width: "10%",
          allowedRoles: defaultRolesAccess,
          sort: this.sortByDateTime,
        },
        {
          text: "CLIENT",
          value: "customer",
          align: "left",
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "TEL",
          value: "customerPhone",
          align: "left",
          allowedRoles: [this.roles.onplaceagent],
        },
        {
          text: "DESTINATION",
          value: "hotelWithDesti",
          align: "left",
          sortable: false,
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "VOLS",
          value: "airlineCodes",
          align: "center",
          sortable: false,
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "TRSF.",
          value: "transferName",
          align: "center",
          sortable: false,
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "FOUR.",
          value: "supplierAccountingNames",
          align: "left",
          sortable: false,
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "MONTANT",
          value: "ca",
          align: "right",
          sortable: false,
          allowedRoles: [this.roles.admin],
        },
        {
          text: "STATUTS",
          value: "bookingStatus",
          align: "left",
          sortable: false,
          allowedRoles: defaultRolesAccess,
        },
        {
          text: "",
          value: "locked",
          sortable: "false",
          allowedRoles: [this.roles.admin],
        },
        {
          text: "LITIGES",
          value: "prestationIssues",
          sortable: "true",
          align: "center",
          allowedRoles: defaultRolesAccess,
        },
      ];

      return headers.filter((item) => {
        return filterHeaders(item.allowedRoles);
      });
    },
  },

  methods: {
    getIssuesSupplierAccountingNames(item) {
      return [...new Set((item.prestationIssues || []).map((pi) => pi.supplierAccountingName).filter(Boolean))]
    },
    getIssueStatusChips(issues) {
      return (this.listing.issueStatusChips || [])
          .map((chip) => {
            const filteredIssues = (issues || []).filter((issue) => issue.status === chip.status)

            return {
              ...chip,
              count: filteredIssues.length,
              issues: filteredIssues,
            }
          })
          .filter((chip) => chip.count > 0)
    },
    onBookingRowClick(item) {
      this.rowClicked(item.bookingId, this.isCtrlOrMetaPressed)
    },
    rowClicked(bookingId, openInNewTab = false) {
      if (openInNewTab) {
        let route = this.$router.resolve({
          name: "Booking",
          params: {id: bookingId},
        });
        window.open(route.href, "_blank");
        return
      }
      this.$router.push({name: "Booking", params: {id: bookingId}});
    },
    load(url, form) {
      this.listing.loading = true;
      this.$emit("loading", this.listing.loading);
      post(url, form).json((res) => {
        this.listing.loading = false;
        this.$emit("loading", this.listing.loading);
        this.listing.bookings = res;
      });
    },
    onKeyDown(event) {
      if (event.ctrlKey || event.metaKey) {
        this.isCtrlOrMetaPressed = true
      }
    },
    onKeyUp(event) {
      if (!event.ctrlKey && !event.metaKey) {
        this.isCtrlOrMetaPressed = false
      }
    },
    onWindowBlur() {
      this.isCtrlOrMetaPressed = false
    },
  }, // [N] methods end
}
</script>


<style scoped>
.gap1 {
  gap: 4px;
}

.issue-chip {
  cursor: zoom-in;
}

.issue-tooltip {
  max-width: 520px;
}

.issue-label-chip {
  max-width: 480px;
  transform: translateX(-2rem);
}

.issue-label-text {
  display: inline-block;
  max-width: 380px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.issue-label-num {
  color: rgba(255, 255, 255, 0.65);
  transform: translateY(+0.075rem);
}

.issue-comment-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.issue-comment-text {
  flex: 1;
  min-width: 260px;
  max-width: 360px;
  white-space: normal;

  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;

  overflow: hidden;
  text-overflow: ellipsis;
}

.issue-comment-meta {
  width: 130px;
  flex-shrink: 0;
}

.caption-xs {
  font-size: 0.60rem;
  font-weight: 400;
  letter-spacing: 0.030em;
  line-height: 1.10rem;
}

.tiny-text{
  font-size: 0.45rem;
}

.suppliers-names {
  text-align: left;
  font-size: 0.55rem;
  font-weight: 400;
  letter-spacing: 0.028em;
  line-height: 0.65rem;
}

</style>